import { fingeringApi, ProcessingError } from '../services/fingeringApi';
import {
  saveFingeringResult,
  loadPendingOriginalPdf,
  loadFingeringResult,
} from './fingeringStorage';
import { markEntryRestored, type JobLifecycleEntry } from './jobLifecycleStorage';
import type { FingeringMode } from '../types/fingering';
import {
  buildBaseName,
  buildResultFileName,
  partNumFromPart,
  resolveOriginalTitle,
} from './fileNaming';

// useJobLifecycleResume.handleDone と同値に揃える (取得 retry 方針 issue #36 F-2c)。
const GET_RESULT_MAX_ATTEMPTS = 3;
const GET_RESULT_RETRY_INTERVAL_MS = 300;
// issue #91: entry.params.fileName が無いとき (異常系 fallback) の base 名。 fileNaming 経由で
// mode 別命名 (part 譜 = 運指結果_運指済.pdf / 総譜 = 運指結果_<1st-4th>_運指済.pdf) に揃える。
const RESUMED_RESULT_BASE_NAME = '運指結果';

/**
 * restoreJobResultCore の結果 (issue #92)。
 * - 'ok': 取得成功し IndexedDB へ保存済み
 * - 'gone': 404 (result 恒久消失)。 retry しても無駄なので即返す。 呼び出し側 (autoRestore) は
 *           done entry を後始末して 404 連発を止める
 * - 'transient-fail': 一時的な取得失敗 (500/network)。 全 retry 後も確定せず。 entry は残す
 */
export type RestoreJobResultOutcome = 'ok' | 'gone' | 'transient-fail';

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * issue #85: done job の結果を取得して IndexedDB へ保存する純関数コア。
 * useJobLifecycleResume.handleDone と自動復元 (FingeringPage) の共通ロジック。
 * 成功で 'ok' / 404 で 'gone' (即返し) / 一時失敗 (全 retry) で 'transient-fail' を返し、
 * 'ok' 以外では IndexedDB を破壊しない (issue #92)。
 * entry の clear (jobLifecycle / 通知既読化) は呼び出し側の責務。
 */
export async function restoreJobResultCore(
  jobId: string,
  entry: JobLifecycleEntry | null,
): Promise<RestoreJobResultOutcome> {
  let result: Awaited<ReturnType<typeof fingeringApi.getJobResult>> | null = null;
  for (let attempt = 0; attempt < GET_RESULT_MAX_ATTEMPTS; attempt++) {
    try {
      result = await fingeringApi.getJobResult(jobId);
      break;
    } catch (e) {
      // 404 (result 恒久消失) は retry しても無駄なので即 'gone' で返す (issue #92)。
      if (e instanceof ProcessingError && e.notFound) {
        return 'gone';
      }
      if (attempt < GET_RESULT_MAX_ATTEMPTS - 1) {
        await sleep(GET_RESULT_RETRY_INTERVAL_MS);
      }
    }
  }
  if (result === null) return 'transient-fail';

  // issue #91: entry.params.fileName があればそれ (投入時 fileNaming で命名済みの正規名) を使い、
  // 無いとき (異常系) のみ fileNaming 経由の fallback 名を生成する (mode 別命名で一貫させる)。
  const fallbackMode = (entry?.params?.mode as FingeringMode | undefined) ?? 'part';
  const fallbackPartNum = partNumFromPart(entry?.params?.part);
  const fileName =
    entry?.params?.fileName ??
    buildResultFileName(buildBaseName(RESUMED_RESULT_BASE_NAME), fallbackPartNum, fallbackMode, 'pdf');
  // issue #152: 復元後の再生成でも命名ソースを保つため元タイトルを持ち越す。 旧 entry
  // (originalTitle 欠落) は完成 PDF 名から derive して救済する (自己治癒)。
  const originalTitle = resolveOriginalTitle({
    originalTitle: entry?.params?.originalTitle,
    fileName,
  });
  // job result には元 PDF が含まれないため、投入時に先行保存した元 PDF を合流させる (設計 §5.2 案A)。
  // issue #126: 先行 PDF が取れない (二重復元 / 書込レース) 場合は破壊的上書きを避け、
  // 既存 results['latest'].originalPdfBlob を温存する (read-merge-write)。
  let originalPdfBlob = (await loadPendingOriginalPdf(jobId)) ?? undefined;
  if (originalPdfBlob === undefined) {
    const existing = await loadFingeringResult();
    originalPdfBlob = existing?.originalPdfBlob;
  }
  await saveFingeringResult({
    pdfBlob: result.pdfBlob,
    fileName,
    originalTitle: originalTitle || undefined,
    audioBlob: result.audioBlob,
    audioFileName: result.audioFileName,
    fingeringJson: result.fingeringJson,
    originalPdfBlob,
    mxlBlob: result.mxlBlob,
    detectedBpm: result.detectedBpm,
    // JobLifecycleParams.mode は string 型 (実値は 'part'|'score') のため FingeringMode へ narrow (#81)。
    mode: entry?.params?.mode as FingeringMode | undefined,
  });
  // issue #126: 先行 PDF (pending-pdfs) の削除は完了モーダル確定 (FingeringPage) へ後ろ倒しした。
  // entry が残る限り先行 PDF も残し、 二重復元での read-merge-write の温存元を維持する。
  // issue #126: IndexedDB 保存完了を localStorage entry へ永続マークし、 再 mount 後の再復元を封じる。
  markEntryRestored(jobId);
  return 'ok';
}
